/* eslint-disable id-length */
/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import FormDialog from '../../../Components/Modal/modal';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import UseAxios from '../../../Api/http/http';
import { fileUploadApi } from '../../../Api/request/fileUploadApi';
import { CANCEL_TEXT, LEAVE_UPLOAD, FILE_UPLOAD } from '../../../Constants/strings';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/en';
import { Typography, Box, CircularProgress } from '@mui/material';
import Papa from 'papaparse';

dayjs.extend(customParseFormat);
dayjs.locale({
  ...dayjs.Ls.en,
  formats: { ...dayjs.Ls.en.formats, LongDate: 'DD-MM-YYYY' },
});

const normalizeHeader = (value) => String(value ?? '').trim()
  .toLowerCase();

const getMissingHeaders = (headers, expectedHeaders) => {
  const headerSet = new Set((headers || []).map(normalizeHeader));
  return expectedHeaders.filter((header) => !headerSet.has(normalizeHeader(header)));
};

const buildHeaderError = (headers, expectedHeaders) => {
  const missingHeaders = getMissingHeaders(headers, expectedHeaders);
  if (missingHeaders.length > 0) {
    return `Missing required column(s): ${missingHeaders.join(', ')}`;
  }
  return 'Invalid file headers.';
};

// eslint-disable-next-line max-lines-per-function
const FileUploadPopUp = ({ openAddModal, handleClose, runBy, onUploadComplete }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [dateRangeError, setDateRangeError] = useState('');
  const [loading, setLoading] = useState(false);
  const { snackbarOpen } = useSnackbar();

  const resetValues = () => {
    setSelectedFile(null);
    setFileError('');
    setDateRangeError('');
  };

  const handleCloseWithReset = () => {
    resetValues();
    handleClose();
  };

  const validateFileType = (fileName) => {
    const validExtensions = ['csv', 'xlsx'];
    return validExtensions.includes(fileName.split('.').pop()
      .toLowerCase());
  };

  const processExcelFile = async (buffer) => {
    const readXlsxFile = (await import('read-excel-file')).default;
    const rows = await readXlsxFile(buffer);

    const expectedHeaders = [
      'project_id',
      'employee_id',
      'activity',
      'efforts',
      'efforts_date',
      'month_end_date',
      'source',
      'comments',
      'darpan_effort_id',
      'jira_status',
      'jira_issue_id',
    ];
    const headers = rows[0];
    // eslint-disable-next-line id-length
    if (getMissingHeaders(headers, expectedHeaders).length > 0) {
      throw new Error(buildHeaderError(headers, expectedHeaders));
    }

    rows.shift();
    const headerIndexMap = new Map(headers.map((header, index) => [normalizeHeader(header), index]));

    return rows
      .map((row) => {
        const logEffortsStr = String(row[headerIndexMap.get(normalizeHeader('efforts'))]).trim(); // Ensure it's a string and trimmed
        const specialCharRegex = /[!@#$%^&*(),?":{}|<>]/g;

        // Remove commas before parsing to handle decimal values correctly
        const logEfforts = parseFloat(logEffortsStr.replace(/,/g, ''));

        if (isNaN(logEfforts) || specialCharRegex.test(logEffortsStr) || logEffortsStr.includes(' ')) {
          throw new Error('Efforts value is invalid or negative.');
        }

        const logEffortsDate = dayjs(row[headerIndexMap.get(normalizeHeader('efforts_date'))]);
        const monthEndDate = dayjs(row[headerIndexMap.get(normalizeHeader('month_end_date'))]);

        if (!logEffortsDate.isValid() || !monthEndDate.isValid()) return null;

        return {
          projectId: row[headerIndexMap.get(normalizeHeader('project_id'))],
          employeeId: row[headerIndexMap.get(normalizeHeader('employee_id'))],
          activityLog: row[headerIndexMap.get(normalizeHeader('activity'))],
          logEfforts,
          logEffortsDate: logEffortsDate.toDate(),
          monthEndDate: monthEndDate.toDate(),
          source: row[headerIndexMap.get(normalizeHeader('source'))],
          comments: row[headerIndexMap.get(normalizeHeader('comments'))],
          jiraIssueId: row[headerIndexMap.get(normalizeHeader('jira_issue_id'))],
          jiraStatus: row[headerIndexMap.get(normalizeHeader('jira_status'))],
          effortStartDate: logEffortsDate.format('YYYY-MM-DD'),
          effortEndDate: monthEndDate.format('YYYY-MM-DD'),
        };
      })

      .filter(Boolean);
  };

  const processCSVFile = (csvText) => {
    return new Promise((resolve, reject) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const headers = results.meta.fields;
          const expectedHeaders = [
            'project_id',
            'employee_id',
            'activity',
            'efforts',
            'efforts_date',
            'month_end_date',
            'source',
            'comments',
            'darpan_effort_id',
            'jira_status',
            'jira_issue_id',
          ];

          if (getMissingHeaders(headers, expectedHeaders).length > 0) {
            reject(new Error(buildHeaderError(headers, expectedHeaders)));
            return;
          }

          const rows = results.data
            .map((row) => {
              const efforts = parseFloat(row['efforts']);
              const effortsString = String(row['efforts']);
              const specialCharRegex = /[!@#$%^&*(),?":{}|<>]/g;
              if (isNaN(efforts) || effortsString.includes(',') || specialCharRegex.test(effortsString) || effortsString.includes(' ')) {
                throw new Error('Efforts value is invalid or negative.');
              }

              const logEffortsDate = dayjs(row['efforts_date'], 'DD/MM/YY', true);
              const monthEndDate = dayjs(row['month_end_date'], 'DD/MM/YY', true);

              if (!logEffortsDate.isValid() || !monthEndDate.isValid()) return null;

              return {
                projectId: row['project_id'],
                employeeId: row['employee_id'],
                activityLog: row['activity'],
                logEfforts: efforts,
                logEffortsDate: logEffortsDate.toDate(),
                monthEndDate: monthEndDate.toDate(),
                source: row['source'],
                comments: row['comments'],
                jiraIssueId: row['jira_issue_id'],
                jiraStatus: row['jira_status'],
                effortStartDate: logEffortsDate.format('YYYY-MM-DD'),
                effortEndDate: monthEndDate.format('YYYY-MM-DD'),
                darpanEffortId: row['darpan_effort_id'],
              };
            })
            .filter(Boolean);

          resolve(rows);
        },
        error: (error) => {
          reject(new Error(error.message));
        },
      });
    });
  };

  const validateDateRange = async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      let rows = null;

      if (file.name.endsWith('.xlsx')) {
        rows = await processExcelFile(buffer);
      } else if (file.name.endsWith('.csv')) {
        const text = new TextDecoder().decode(buffer);
        rows = await processCSVFile(text);
      } else {
        throw new Error('Invalid file type');
      }

      const lastMonthStart = dayjs().subtract(1, 'month')
        .startOf('month');
      const nextMonthEnd = dayjs().add(1, 'month')
        .endOf('month');

      const isValidRange = rows.every(({ logEffortsDate, monthEndDate }) => {
        const start = dayjs(logEffortsDate);
        const end = dayjs(monthEndDate);
        return start.isValid() && end.isValid() && end.isBetween(lastMonthStart, nextMonthEnd, null, '[]');
      });

      if (!isValidRange) {
        setDateRangeError('The start date in the file is out of range for current configured dates.');
        return false;
      }

      return true;
    } catch (error) {
      setDateRangeError(error.message.includes('headers')
        ? 'Invalid file format. Please use the correct template.'
        : error.message,
      );
      return false;
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return setFileError('Please select a file to upload.');

    if (!validateFileType(selectedFile.name)) {
      return setFileError('Allowed file types: CSV, XLSX.');
    }

    const MAX_FILE_SIZE_MB = 10;
    if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return setFileError(`File size should not exceed ${MAX_FILE_SIZE_MB} MB.`);
    }

    const isValidDateRange = await validateDateRange(selectedFile);
    if (!isValidDateRange) return;

    setLoading(true);

    const requestData = {
      schedulerType: 'client-effort-log',
      fileCategory: selectedFile.name.split('.').pop(),
      runBy,
      file: selectedFile,
    };

    try {
      const response = await UseAxios(
        fileUploadApi(requestData.schedulerType, requestData.fileCategory, requestData.runBy, requestData.file),
      );

      if (response.status === 200) {
        if (response.data?.status === 'Completed') {
          snackbarOpen(FILE_UPLOAD.UPLOAD_SUCCESS, 'success');
        }
      } else {
        const responseData = response.data?.response?.data || response.data || {};
        const details = Array.isArray(responseData?.details) ? responseData.details.join(' | ') : '';
        const errorMessage = details
          ? `${responseData?.message || responseData?.error || FILE_UPLOAD.UNABLE_TO_FILE_UPLOAD}: ${details}`
          : responseData?.error?.message || responseData?.message || FILE_UPLOAD.UNABLE_TO_FILE_UPLOAD;
        snackbarOpen(errorMessage, 'error');
      }

      // Notify parent component that upload is complete (success or failure)
      onUploadComplete();
      handleCloseWithReset();
    } catch (error) {
      const errorData = error.response?.data || error?.data || {};
      const details = Array.isArray(errorData?.details) ? errorData.details.join(' | ') : '';
      const errorMessage = details
        ? `${errorData?.message || FILE_UPLOAD.UNABLE_TO_FILE_UPLOAD}: ${details}`
        : errorData?.message || FILE_UPLOAD.UNABLE_TO_FILE_UPLOAD;
      snackbarOpen(errorMessage, 'error');
      // Notify parent component that upload is complete (with failure)
      onUploadComplete();
      handleCloseWithReset();
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setFileError('');
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    handleFileChange({ target: { files: [file] } });
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <FormDialog
      title={FILE_UPLOAD.POPUP_TITLE}
      open={openAddModal}
      button1Action={handleCloseWithReset}
      dialogPaperCss="leave-upload-dialog-paper"
      dialogCss="leave-upload-dialog"
      dialogBodycontent={
        <div className="leave-upload-dialog-body">
          <Typography variant="caption" className="file-validation">Select file to upload*</Typography>
          <Box
            className="leave-upload-box"
            sx={{ display: 'flex', gap: '1rem', alignItems: 'center', cursor: 'pointer', marginTop: '1%' }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <label htmlFor="file-upload" className="file-upload-button">Choose File</label>
            <input id="file-upload" type="file" className="file-input" onChange={handleFileChange} style={{ display: 'none' }} />
            <span className="file-name">{selectedFile ? selectedFile.name : 'No file chosen'}</span>
          </Box>
          {fileError && <Typography className="error-message" color="error">{fileError}</Typography>}
          {dateRangeError && <Typography sx={{fontSize: 'small'}} className="error-message" color="error">{dateRangeError}</Typography>}
        </div>
      }
      button1={CANCEL_TEXT}
      button2={loading ? <CircularProgress color='inherit' size={20} /> : LEAVE_UPLOAD.UPLOAD}
      button2Action={handleFileUpload}
      cancelButtonId="manual-leave-upload-cancel-btn"
      submitButtonId="manual-leave-upload-submit-btn"
      isPopUp={true}
      button2Disabled={loading === true}
    />
  );
};

FileUploadPopUp.propTypes = {
  openAddModal: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  runBy: PropTypes.string.isRequired,
  onUploadComplete: PropTypes.func.isRequired,
};

export default FileUploadPopUp;
